import React, { useState } from "react";
import { useControles } from "../../context/controles.context";
import { PrimaryLabel, PrimaryTextInput, PrimaryButton, PrimaryInputMask, ErrorMsg } from "../../globalComponents";
import { StyledDiv, PageWrapper, FormsContainer, Forms, StyledDivDesktop, PageWrapperDesktop } from "./styles";


import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../context/auth.context";
import { useWhitelabel } from "../../context/whitelabel.context";

const CardRegister = () => {
    const { isMobile, setErrorMsg } = useControles()
    const navigate = useNavigate()
    const { Register, Login, setAuthStatus, setVerifiedUser, setIsAdmin, ClearAdminAccessMode } = useAuth()
    const { logoVertical } = useWhitelabel()

    const [formValues, setFormValues] = useState({
        name: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        phone: ''
    })
    const [fieldErrors, setFieldErrors] = useState({})
    const [apiError, setApiError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const getPhoneDigits = (value) => value.replace(/\D/g, '')

    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(value)
    }

    const validateField = (fieldName, value) => {
        const trimmedValue = (value || '').trim()

        if (fieldName === 'name' && !trimmedValue) {
            return 'Nome obrigatorio.'
        }

        if (fieldName === 'email') {
            if (!trimmedValue) {
                return 'Email obrigatorio.'
            }
            if (!validateEmail(trimmedValue)) {
                return 'Informe um email valido.'
            }
        }

        if (fieldName === 'username') {
            if (!trimmedValue) {
                return 'Username obrigatorio.'
            }
            if (trimmedValue.length < 3) {
                return 'Username deve ter no minimo 3 caracteres.'
            }
        }

        if (fieldName === 'password') {
            if (!value) {
                return 'Senha obrigatoria.'
            }
            if (value.length < 8) {
                return 'Senha deve ter no minimo 8 caracteres.'
            }
        }

        if (fieldName === 'confirmPassword') {
            if (!value) {
                return 'Confirme sua senha.'
            }
            if (value !== formValues.password) {
                return 'As senhas nao coincidem.'
            }
        }

        if (fieldName === 'phone') {
            const digits = getPhoneDigits(value || '')
            if (digits && digits.length !== 10 && digits.length !== 11) {
                return 'Celular invalido. Informe 10 ou 11 digitos.'
            }
        }

        return ''
    }

    const validateForm = () => {
        const nextErrors = {
            name: validateField('name', formValues.name),
            email: validateField('email', formValues.email),
            username: validateField('username', formValues.username),
            password: validateField('password', formValues.password),
            confirmPassword: validateField('confirmPassword', formValues.confirmPassword),
            phone: validateField('phone', formValues.phone)
        }

        setFieldErrors(nextErrors)
        const hasError = Object.values(nextErrors).some((msg) => Boolean(msg))
        return {
            isValid: !hasError,
            errors: nextErrors
        }
    }

    const handleChange = (fieldName, value) => {
        setFormValues((prev) => ({
            ...prev,
            [fieldName]: value
        }))

        if (fieldName === 'password' && formValues.confirmPassword) {
            setFieldErrors((prev) => ({
                ...prev,
                confirmPassword: validateField('confirmPassword', formValues.confirmPassword)
            }))
        }

        if (fieldErrors[fieldName]) {
            setFieldErrors((prev) => ({
                ...prev,
                [fieldName]: validateField(fieldName, value)
            }))
        }

        if (apiError) {
            setApiError('')
        }
    }

    const handleBlur = (fieldName) => {
        setFieldErrors((prev) => ({
            ...prev,
            [fieldName]: validateField(fieldName, formValues[fieldName])
        }))
    }

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleRegister();
        }
    }

    const handleRegister = async () => {
        const validation = validateForm()

        if (!validation.isValid) {
            const firstError = Object.values(validation.errors).find((value) => Boolean(value))
            setErrorMsg(firstError || 'Preencha os campos obrigatorios corretamente.')
            return false
        }

        const digits = getPhoneDigits(formValues.phone)
        const payload = {
            name: formValues.name.trim(),
            email: formValues.email.trim().toLowerCase(),
            username: formValues.username.trim(),
            password: formValues.password,
            ...(digits ? { phone: digits } : {})
        }

        setIsSubmitting(true)

        try {
            await Register(payload)
            await Login({
                username: payload.email,
                password: payload.password
            })

            setErrorMsg(null)
            setApiError('')

            // Todo cadastro novo deve seguir para a etapa de verificacao.
            setAuthStatus(true)
            setVerifiedUser(false)
            setIsAdmin(false)
            ClearAdminAccessMode()
            navigate('/')
        } catch (error) {
            const errorCode = error?.response?.data?.error?.code
            const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.message || 'Nao foi possivel concluir o cadastro.'

            if (errorCode === 'AUTH_EMAIL_ALREADY_EXISTS') {
                setFieldErrors((prev) => ({ ...prev, email: 'Email ja cadastrado.' }))
            }

            if (errorCode === 'AUTH_USERNAME_ALREADY_EXISTS') {
                setFieldErrors((prev) => ({ ...prev, username: 'Username ja cadastrado.' }))
            }

            if (errorCode === 'AUTH_INVALID_CREDENTIALS') {
                setErrorMsg('Cadastro concluido, mas nao foi possivel entrar automaticamente. Faca login para continuar.')
                setApiError('Cadastro concluido, mas nao foi possivel entrar automaticamente. Faca login para continuar.')
                return
            }

            setErrorMsg(errorMessage)
            setApiError(errorMessage)
        } finally {
            setIsSubmitting(false)
        }

    }

    const handleBackToLogin = () => {
        navigate('/')
    }

    const renderForms = () => (
        <>
            <Forms>
                <div>
                    <PrimaryLabel>Nome</PrimaryLabel>
                    <PrimaryTextInput
                        placeholder="Informe seu nome"
                        value={formValues.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        onBlur={() => handleBlur('name')}
                        onKeyDown={handleKeyPress}
                    />
                    {fieldErrors.name && <ErrorMsg>{fieldErrors.name}</ErrorMsg>}
                </div>

                <div>
                    <PrimaryLabel>Email</PrimaryLabel>
                    <PrimaryTextInput
                        placeholder="Informe seu melhor email"
                        value={formValues.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        onBlur={() => handleBlur('email')}
                        onKeyDown={handleKeyPress}
                    />
                    {fieldErrors.email && <ErrorMsg>{fieldErrors.email}</ErrorMsg>}
                </div>

                <div>
                    <PrimaryLabel>Username</PrimaryLabel>
                    <PrimaryTextInput
                        placeholder="Informe seu username"
                        value={formValues.username}
                        onChange={(e) => handleChange('username', e.target.value)}
                        onBlur={() => handleBlur('username')}
                        onKeyDown={handleKeyPress}
                    />
                    {fieldErrors.username && <ErrorMsg>{fieldErrors.username}</ErrorMsg>}
                </div>

                <div>
                    <PrimaryLabel>Celular (opcional)</PrimaryLabel>
                    <PrimaryInputMask
                        mask="(99) 99999-9999"
                        placeholder="Informe seu celular"
                        type="text"
                        value={formValues.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        onBlur={() => handleBlur('phone')}
                        onKeyDown={handleKeyPress}
                    />
                    {fieldErrors.phone && <ErrorMsg>{fieldErrors.phone}</ErrorMsg>}
                </div>

                <div>
                    <PrimaryLabel>Senha</PrimaryLabel>
                    <PrimaryTextInput
                        placeholder="Informe sua senha"
                        type="password"
                        value={formValues.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        onBlur={() => handleBlur('password')}
                        onKeyDown={handleKeyPress}
                    />
                    {fieldErrors.password && <ErrorMsg>{fieldErrors.password}</ErrorMsg>}
                </div>

                <div>
                    <PrimaryLabel>Confirmar senha</PrimaryLabel>
                    <PrimaryTextInput
                        placeholder="Confirme sua senha"
                        type="password"
                        value={formValues.confirmPassword}
                        onChange={(e) => handleChange('confirmPassword', e.target.value)}
                        onBlur={() => handleBlur('confirmPassword')}
                        onKeyDown={handleKeyPress}
                    />
                    {fieldErrors.confirmPassword && <ErrorMsg>{fieldErrors.confirmPassword}</ErrorMsg>}
                </div>
            </Forms>

            {apiError && <ErrorMsg>{apiError}</ErrorMsg>}

            <div style={{ paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 30 }}>
                <PrimaryButton onClick={handleRegister} onKeyDown={handleKeyPress} disabled={isSubmitting}>
                    {isSubmitting ? 'Cadastrando...' : 'Cadastrar-se'}
                </PrimaryButton>

                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                    <p>Caso ja possua uma conta,</p>
                    <span onClick={() => {
                        navigate('/')
                    }}>faça seu login!</span>
                </div>
            </div>
        </>
    )

    return (
        <>
            {isMobile ? 
                <>
                <StyledDiv>
                    <PageWrapper>
                        <div style={{
                            display: 'flex', flexDirection: 'column', textAlign: 'left', justifyContent: 'left', gap: 12, paddingBottom: 20, width: '80%',
                            maxWidth: '420px'
                            }}>

                            <button
                                onClick={handleBackToLogin}
                                style={{
                                    width: 'fit-content',
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--Primary-text)',
                                    fontSize: 16,
                                    padding: 0,
                                    cursor: 'pointer',
                                    opacity: 0.9
                                }}
                            >
                                Voltar
                            </button>

                            <h1 style={{fontSize: 36, textAlign: 'left', width: '100%'}}>Cadastro</h1>
                            
                        </div>
                        
                        <FormsContainer>
                            {renderForms()}
                        </FormsContainer>
                    </PageWrapper>
                </StyledDiv>
                </> : 
                <>
                <StyledDivDesktop>
                    <div style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',    height: '100%' ,backgroundImage: 'var(--Background-image)', backgroundPosition: 'center', backgroundSize: 'cover', backgroundRepeat: 'no-repeat'}}>
                        <img src={logoVertical} style={{width: '100%', height: '100%', maxWidth: 200, maxHeight: 200}}/>
                    </div>

                    <PageWrapperDesktop>

                        
                  
                        <div style={{display: 'flex', flexDirection: 'column', textAlign: 'center', gap: 10, paddingBottom: 30, maxWidth: '420px', width: '80%'}}>
                            <h1 style={{fontSize: 30, textAlign: 'left', maxWidth: '420px'}}>
                                Cadastro
                            </h1>
                        </div>
                        
                        <FormsContainer>
                            {renderForms()}
                        </FormsContainer>
                    </PageWrapperDesktop>
                </StyledDivDesktop>
                </>
            }
        </>
    )
}

export default CardRegister